import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon, Terminal as TerminalIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { nasService } from '../../services/nasService';

interface RadiusStatusModalProps {
  open: boolean;
  onClose: () => void;
}

const RadiusStatusModal: React.FC<RadiusStatusModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [log, setLog] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showStatus, setShowStatus] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nasService.getRadiusStatus();
      setStatus(data.status);
      setLog(data.log);
    } catch (err: any) {
      console.error('Erro ao buscar status do Radius:', err);
      setError(err.response?.data?.message || 'Erro ao comunicar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = async () => {
    try {
      setLoading(true);
      await nasService.restartRadius();
      enqueueSnackbar('Serviço Freeradius reiniciado com sucesso!', { variant: 'success' });
      // Atualizar o log/status 1 segundo depois para dar tempo do serviço subir
      setTimeout(() => fetchStatus(), 1000);
    } catch (err: any) {
      console.error('Erro ao reiniciar Radius:', err);
      enqueueSnackbar(err.response?.data?.message || 'Erro ao reiniciar o serviço.', { variant: 'error' });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStatus();
    } else {
      setStatus('');
      setLog('');
      setError(null);
    }
  }, [open]);

  const getStatusIndicator = (statusStr: string) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    if (!statusStr) return null;
    if (statusStr.includes('Active: active (running)')) {
      return { label: 'Online (Rodando)', color: 'success' as const };
    }
    if (statusStr.includes('Active: failed')) {
      return { label: 'Parado (Falha)', color: 'error' as const };
    }
    if (statusStr.includes('Active: inactive')) {
      return { label: 'Inativo (Parado)', color: 'warning' as const };
    }
    return { label: 'Offline / Erro', color: 'error' as const };
  };

  const indicator = getStatusIndicator(status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
    >
      <DialogTitle sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
            <TerminalIcon color="primary" sx={{ fontSize: { xs: 22, sm: 26 }, flexShrink: 0 }} />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Status do Serviço FreeRADIUS
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} size="small" aria-label="close">
            <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ bgcolor: '#f5f5f5', p: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} minHeight={250} flex={1}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Buscando informações do servidor...
            </Typography>
          </Box>
        ) : error ? (
          <Box p={4} minHeight={250} display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <Box p={{ xs: 1.5, sm: 2 }} sx={{ maxHeight: isMobile ? 'none' : '70vh', overflowY: 'auto', flex: 1 }}>
            <Box mb={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showStatus}
                    onChange={(e) => setShowStatus(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Exibir Systemctl Status (freeradius)
                  </Typography>
                }
              />
            </Box>

            {showStatus && (
              <Box
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  p: { xs: 1.25, sm: 2 },
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  mb: 2,
                  overflowX: 'auto',
                }}
              >
                {status || 'Nenhum status disponível'}
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Últimas 50 linhas do log
            </Typography>
            <Box
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                p: { xs: 1.25, sm: 2 },
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                overflowX: 'auto',
              }}
            >
              {log || 'Log vazio'}
            </Box>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: { xs: '100%', sm: 'auto' },
            mr: { xs: 0, sm: 'auto' },
            gap: 1,
          }}
        >
          {indicator && (
            <Chip
              label={indicator.label}
              color={indicator.color}
              size="small"
              sx={{ height: 24, fontSize: '0.75rem', fontWeight: 'bold' }}
            />
          )}
          <Button
            onClick={handleRestart}
            disabled={loading}
            color="warning"
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<RestartAltIcon />}
            sx={{ flexShrink: 0 }}
          >
            Reiniciar Serviço
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={fetchStatus}
            disabled={loading}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Atualizar Dados
          </Button>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Fechar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RadiusStatusModal;

